import { useState } from "react";
import { Box, Grid, MenuItem, TextField, Typography } from "@mui/material";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { PrimaryButton, Section } from "../ui/primitives";
import { colors, company } from "../ui/tokens";
import PageHero from "../widgets/PageHero";

const TOPICS = ["project", "oxipos", "support", "partner"];

function Detail({ label, children }) {
    return (
        <Box sx={{ py: 2.25, borderTop: `1px solid ${colors.line}` }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: colors.muted, mb: 0.75 }}>
                {label}
            </Typography>
            <Box sx={{ color: colors.ink, fontSize: "1.02rem", lineHeight: 1.7 }}>{children}</Box>
        </Box>
    );
}

export function Contact() {
    const { t } = useTranslation("ContactPage");
    const [form, setForm] = useState({ name: "", organisation: "", email: "", topic: "project", message: "" });
    const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });

    // There is no backend for this site, so the form composes an email.
    const send = (e) => {
        e.preventDefault();
        const body = [
            `${t("form.name")}: ${form.name}`,
            `${t("form.organisation")}: ${form.organisation}`,
            `${t("form.email")}: ${form.email}`,
            `${t("form.topic")} ${t(`form.topics.${form.topic}`)}`,
            "",
            form.message,
        ].join("\n");
        const subject = `${t("form.subject")} · ${t(`form.topics.${form.topic}`)}`;
        window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    };

    const field = { fullWidth: true, variant: "outlined", sx: { "& .MuiOutlinedInput-root": { backgroundColor: colors.paper } } };

    return (
        <>
            <Helmet>
                <title>{t("meta.title")}</title>
                <meta name="description" content={t("meta.description")} data-rh="true" />
                <link rel="canonical" href="https://oxide-digital.com/contact" />
            </Helmet>
            <PageHero eyebrow={t("hero.eyebrow")} title={t("hero.title")} lead={t("hero.lead")} />

            <Section tone="paper" py={{ xs: 7, md: 11 }}>
                <Grid container spacing={{ xs: 7, md: 10 }}>
                    <Grid item xs={12} md={7}>
                        <Typography variant="h3" component="h2" sx={{ mb: 4 }}>{t("form.title")}</Typography>
                        <Box component="form" onSubmit={send}>
                            <Grid container spacing={2.5}>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...field} required label={t("form.name")} value={form.name} onChange={update("name")} autoComplete="name" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...field} label={t("form.organisation")} value={form.organisation} onChange={update("organisation")} autoComplete="organization" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...field} required type="email" label={t("form.email")} value={form.email} onChange={update("email")} autoComplete="email" />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField {...field} select label={t("form.topic")} value={form.topic} onChange={update("topic")}>
                                        {TOPICS.map((k) => (
                                            <MenuItem key={k} value={k}>{t(`form.topics.${k}`)}</MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        {...field}
                                        required
                                        multiline
                                        minRows={6}
                                        label={t("form.message")}
                                        helperText={t("form.messageHint")}
                                        value={form.message}
                                        onChange={update("message")}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <PrimaryButton type="submit">{t("form.submit")}</PrimaryButton>
                                    <Typography sx={{ mt: 1.5, fontSize: "0.85rem", color: colors.muted }}>{t("form.note")}</Typography>
                                </Grid>
                            </Grid>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={5}>
                        <Box sx={{ backgroundColor: colors.paperWarm, border: `1px solid ${colors.line}`, borderRadius: "16px", px: { xs: 3, md: 4 }, py: { xs: 1.5, md: 2.5 } }}>
                            <Typography variant="h4" component="h2" sx={{ py: 2 }}>{t("direct.title")}</Typography>
                            <Detail label={t("direct.email")}>
                                <Box component="a" href={`mailto:${company.email}`} sx={{ color: "inherit", fontWeight: 600 }}>{company.email}</Box>
                            </Detail>
                            <Detail label={t("direct.phone")}>
                                <Box component="a" href={company.phoneHref} sx={{ color: "inherit", fontWeight: 600 }}>{company.phone}</Box>
                            </Detail>
                            <Detail label={t("direct.whatsapp")}>
                                <Box component="a" href={company.whatsappHref} target="_blank" rel="noopener noreferrer" sx={{ color: "inherit", fontWeight: 600 }}>
                                    {t("direct.whatsappCta")}
                                </Box>
                            </Detail>
                            <Detail label={t("direct.office")}>
                                {company.legalName}<br />
                                {company.street}<br />
                                {company.district}<br />
                                {company.city}, {company.country}
                            </Detail>
                        </Box>
                    </Grid>
                </Grid>
            </Section>
        </>
    );
}
